import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Container } from "@material-ui/core";
import { RouteComponentProps } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import RepairJourney from "../RepairJourney";
import LoadingSpinner from "../../storybook/LoadingSpinner";
import { Helper } from "../../utils/Helper/HelperUtil";
import _ from "lodash";
import * as api from "../../api";
import RepairDiagnosticsSessionResponse from "../../models/Diagnostics/RepairDiagnosticsSessionResponse";
import { WorksOrderRequest } from "../../models/WorksOrders/WorksOrderRequest";
import { CurrentRepair } from "../../models/Repair/Repair";
import Address from "../../models/Customer/Address";
import CustomerName from "../../models/Customer/CustomerName";
import { PremisesTypeId } from "../../models/Customer/Premises";
import { RepairPriorityCode } from "../../models/Repair/CreateRepair";
import { getCustomerId, getMaintResp, getRepairResp, saveCustomerDetails, workOrderRepairObject } from "../../ducks/redux/actions/getAddress";
import { dmcCheckSavedAnswer, loadDuplicateRepairPage, resumeDraftRepair, setRepairDiagnosticFinalData, userChosenAdditionalDescription, userChosenAdditionalDescriptionCOUNT, userChosenCovidQuestion, userChosenDuplicateQuestion, userChosenEligibilityErrorCode, userChosenEmergencyQuestion, userChosenHighLevelDescription, userChosenNotifyAll, userChosenRepairLocationId, userChosenResumeRepair, userChosenSaveExit, userChosenSOR, userChosenSupplementaryQuestions, userChosenVulnerabilityAdditionalInformation, userChosenVulnerabilityQuestion, workOrderData, backToRepairAsset } from "../../ducks/redux/actions/userChoices";
import { priorityCode, selectedRepairReasonDefaultPriority, selectedRepairReasonPriorityOverride } from "../../ducks/redux/actions/dashboardReducer.actions";
import { logError } from "../../utils/Helper/logError";
import ExitModalAndSave from "../Modal/ExitModalAndSave";
import { nanoid } from "nanoid";
import { PermissionsList } from "../../models/Permissions";
import { GetRepairEligibilityOptions } from "../../api";


const IFRAME_CLASSNAME = "w-100 position-relative" as const;
const CONTAINER_SELECTOR = ".container.w-100.position-relative" as const;
const INTEGRATION_TYPE = "loggedjobjscallback" as const;
const TENANCY_GROUP_NAMESPACE = "plentific-tenancy-groups-mapping" as const;
const SELECTED_REPORTING_METHOD = "ColleagueSelfServe" as const;
const DIVIDED_BLOCK = Helper.AssetTypeName["Divided Block"];
const DIAGNOSTICS_SESSION_LOADER = "diagnostics-session-loader" as const;
const SCRIPT_ID = Helper.ActiveHousingScriptId;

declare global {
  interface Window {
    activeHousingIframeConfig?: any;
  }
}

interface RepairEnhancedDiagnosticProps extends RouteComponentProps<any> { }

const RepairEnhancedDiagnostic: React.FC<RepairEnhancedDiagnosticProps> = (props) => {
  const timeoutIdsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const PARENT_IFRAME_ID = useMemo(() => `containerelementID-${nanoid()}`, []);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const hasInitialized = useRef(false);
  const assetId = useSelector((state: any) => state?.dashboardReducer?.formState?.customerID?.assetId);
  const assetDetails = useSelector((state: any) => state?.dashboardReducer?.formState?.assetByIdDataObject);
  const customerID = useSelector((state: any) => state?.addresses?.customerid);
  const customerDetails = useSelector((state: any) => state?.addresses?.saveCustomerDetails);
  const selectedTenancyGroup = useSelector((state: any) => state?.userChoicesReducer?.selectedTenancyGroup);
  const tenanciesData = useSelector((state: any) => state?.addresses?.customertenancies);
  const sordamp = useSelector((state: any) => state?.financialReferralReducer?.sordamp);
  const isCatalyst = useSelector((state: any) => state?.userChoicesReducer?.isCatalystProperty);
  const isEligibleForEnhancedDiagnostics = useSelector((state: any) => state?.addresses?.isEligibleForEnhancedDiagnostics);
  const isEligibleTradeforED = useSelector((state: any) => state?.userChoicesReducer?.isEligibleTradeforED);
  const existingWorkOrder = useSelector((state: any) => state?.addresses?.workOrderRepair);
  const plentificCategoryId = (props.location.state as any)?.plentificCategoryId;
  const [isBackModalOpen, setIsBackModalOpen] = useState(false);
  const customerAdditionalAlertData = useSelector((state: any) => state?.userChoicesReducer?.customerAdditionalAlertData);
  const isAnaTypeValue = customerAdditionalAlertData?.length > 0;
  const sessionIdRef = useRef<string>("");
  const propertyType = assetDetails?.assetType === Helper.AssetTypeName.Property ? "PRO" : "COM";
  const getPermissionsData = useSelector((state: any) => state?.userChoicesReducer?.userProfilePermissions);
  const workorderChoosePriorityOfRepairPermission = Helper.doesHavePermission(getPermissionsData, PermissionsList.workorder_choose_priority_of_repair);
  const CUSTOMER_STG = useSelector((state: any) => state?.userChoicesReducer?.edConfigCustomer);
  const ACTIVE_HOUSING_STAGING_REPAIRS_URL = useSelector((state: any) => state?.userChoicesReducer?.edConfigOverrideUrl);
  const ACTIVE_HOUSING_URL = useSelector((state: any) => state?.userChoicesReducer?.edConfigActiveHousingScriptUrl);
  const isHHSRSEnabled = useSelector((state: any) => state?.financialReferralReducer?.isHHSRSEnabled)
  const selectedRepairReason = useSelector((state: any) =>state?.dashboardReducer?.formState?.selectedRepairReasonObject);
  const isHHSRSRole = Array.isArray(getPermissionsData) && getPermissionsData.includes(PermissionsList.update_digital_journey_hhsrs_hazard_repair);
  // Show/hide loader and iframe during key loading and work order actions
  const showLoader = useCallback((isShow = true) => {
    const iframeDiv = document.getElementById(PARENT_IFRAME_ID);
    const loaderDiv = document.getElementById(DIAGNOSTICS_SESSION_LOADER);
    iframeDiv && iframeDiv.classList[isShow ? "add" : "remove"]("d-none");
    loaderDiv && loaderDiv.classList[isShow ? "remove" : "add"]("d-none");
  }, [PARENT_IFRAME_ID]);

  // Appends env to sessionId if not prod, helps track by environment
  const getSessionIdWithEnv = (sessionId: string): string => {
    const env = process.env.REACT_APP_ENV;
    if (env && env.toLowerCase() !== "prod" && sessionId) {
      return `${sessionId}_${env}`;
    }
    return sessionId;
  };

  // Look up Plentific tenancy group from reference data API
  const getMatchingTenancyGroup = useCallback(
    async (tenancyGroup: string): Promise<string> => {
      try {
        const refData = await api.getReferenceData(TENANCY_GROUP_NAMESPACE);
        const keyValues = refData?.[0]?.namespace?.keyValues ?? {};
        const foundKey = _.findKey(
          keyValues,
          (value: any) => value.tenancyGroup === tenancyGroup
        );
        return foundKey ? keyValues[foundKey]?.plentificTenancyGroup : "";
      } catch (e) {
        logError("getReferenceData (tenancy group) failed", e);
        return "";
      }
    },
    []
  );

  // Remove the diagnostics iframe container div from the DOM, if present
  const removeIframeContainer = useCallback(() => {
    requestAnimationFrame(() => {
      const existingDiv = document.getElementById(PARENT_IFRAME_ID);
      if (existingDiv && existingDiv.parentNode) {
        existingDiv.parentNode.removeChild(existingDiv);
      }
    });
  }, [PARENT_IFRAME_ID]);

  // Add the diagnostics iframe container div to the DOM for Active Housing integration
  const createIframeContainer = useCallback(() => {
    requestAnimationFrame(() => {
      const div = document.createElement("div");
      div.id = PARENT_IFRAME_ID;
      div.className = IFRAME_CLASSNAME;
      const parent = document.querySelector(CONTAINER_SELECTOR);
      if (parent) {
        parent.appendChild(div);
      }
    });
  }, [PARENT_IFRAME_ID]);

  // Load/reload the Active Housing script to enable diagnostics iframe features
  const injectActiveHousingScript = () => {
    requestAnimationFrame(() => {
      const oldScript = document.getElementById(SCRIPT_ID);
      if (oldScript) oldScript.remove();

      const script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.src = `${ACTIVE_HOUSING_URL}`;
      script.async = true;
      document.body.appendChild(script);
    });
  };

  // Delete the previous work order if one exists (clean slate before creating new)
  const deleteExistingWorkOrder = useCallback(async () => {
    try {
      await api.deleteWorkOrder(existingWorkOrder?.id, existingWorkOrder?.eTag?.toString());
    } catch (err) {
      logError("Error deleting previous work order", err);
    }
  }, [existingWorkOrder]);

  // Waits for iframe div and config to be present, then injects ActiveHousing script
  // Uses requestAnimationFrame to poll DOM at animation frame speed—this avoids race conditions
  const waitForIframeAndInjectScript = async (PARENT_IFRAME_ID: string, SCRIPT_ID: string, injectActiveHousingScript: any) => {
    let hasScriptBeenInjected = false;
    const checkAndInject = async () => {
      const iframeDiv = document.getElementById(PARENT_IFRAME_ID);
      const isConfigReady = typeof window.activeHousingIframeConfig !== "undefined";
      if (iframeDiv && isConfigReady && !hasScriptBeenInjected) {
        hasScriptBeenInjected = true;
        await injectActiveHousingScript();
      } else if (!hasScriptBeenInjected) {
        requestAnimationFrame(checkAndInject);
      }
    };
    await checkAndInject();
  };

  // Watches the DOM for the insertion of the diagnostics iframe, then disables loading state
  // Uses MutationObserver to automatically detect when the iframe element appears
  function handleIframeLoad(
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
    timeoutIdsRef: React.MutableRefObject<ReturnType<typeof setTimeout>[]>
  ): void {
    const timeoutId = setTimeout(() => {
      setIsLoading(false);
    }, 0);
    timeoutIdsRef.current.push(timeoutId);
  }

  // Watches the DOM for the insertion of the diagnostics iframe, then disables loading state
  // Uses MutationObserver to automatically detect when the iframe element appears
  const observeIframeAndSetLoading = (
    CONTAINER_SELECTOR: string,
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>
  ): MutationObserver => {
    let observer: MutationObserver;

    // define the callback as a closure, not with .bind
    const mutationCallback: MutationCallback = () => {
      const iframe = document.querySelector(
        `iframe[name^='active-housing-iframe']`
      );
      if (iframe instanceof HTMLIFrameElement) {
        observer.disconnect();
        iframe.addEventListener("load", () =>
          handleIframeLoad(setIsLoading, timeoutIdsRef)
        );
      }
    };

    observer = new MutationObserver(mutationCallback);

    // Start watching the parent container for child DOM changes
    const iframeContainer = document.querySelector(CONTAINER_SELECTOR);
    if (iframeContainer) {
      observer.observe(iframeContainer, { childList: true, subtree: true });
    }
    return observer;
  };


  // Reset Redux state to default when starting diagnostics from new/duplicate/emergency repair
  const resetReduxFromNewEmergencyAndDuplicatePages = useCallback(() => {
    dispatch(userChosenEmergencyQuestion(""));
    dispatch(userChosenNotifyAll(null));
    dispatch(userChosenCovidQuestion(""));
    dispatch(userChosenVulnerabilityQuestion("No"));
    dispatch(userChosenVulnerabilityAdditionalInformation(""));
    dispatch(userChosenDuplicateQuestion(""));
    dispatch(userChosenAdditionalDescription(""));
    dispatch(userChosenAdditionalDescriptionCOUNT(0));
    dispatch(userChosenSupplementaryQuestions(""));
    dispatch(dmcCheckSavedAnswer(""));
    dispatch(userChosenSaveExit(false));
    dispatch(resumeDraftRepair(false));
    dispatch(userChosenResumeRepair(false));
    dispatch(loadDuplicateRepairPage(false));
  }, [dispatch]);

  // Calls API to get eligibility, always returns a result (or empty)
  const getEligibilityResponse = useCallback(
    async (
      api: any,
      assetDetails: any,
      propertyAttribute: any,
      completeEDSessionData: any,
      logError: Function
    ) => {
      try {
        const options: GetRepairEligibilityOptions = {
          assetType: assetDetails?.assetType,
          maintenanceResponsibility: propertyAttribute,
          repairResponsibility: completeEDSessionData?.elementCode,
          additionalRepairInfo: false,
          isRepairEligible: true,
          isCustomerView: true,
          sorCode: completeEDSessionData?.sorCode,
          uprn: assetDetails?.uprn,
          isInDefects: assetDetails?.statuses?.isInDefects ?? false,
          heatingRepairContext: "",
          assetSubType: assetDetails?.assetSubType,
          parentAssetType: assetDetails?.parentAssetType
        }
        return await api.getRepairEligibility(options);
      } catch (eligibilityError) {
        logError("getRepairEligibility failed", eligibilityError);
        return [];
      }
    },
    []
  );

  // Look up repair location ID from API reference data for asset type/location
  const getRepairLocationId = useCallback(
    async (assetType: string, location: string): Promise<string> => {
      try {
        // As ED doesnt have any logic to show locations as per asset type, any location can be passed
        // hence retrieving all location codes
        const refData = await api.getReferenceData("Property");
        const keyValues =
          refData?.[0]?.namespaces?.[0]?.namespace?.keyValues ?? {};
        return _.findKey(keyValues, (value) => value === location) ?? "";
      } catch (e) {
        logError("getReferenceData failed", e);
        return "";
      }
    },
    []
  );

  // Determines/gets priority using API call with form data
  const fetchDeterminedPriority = useCallback(
    async (
      api: any,
      workOrderRepair: any,
      isAnaTypeValue: any,
      customerDetails: any,
      completeEDSessionData: any,
      assetDetails: any
    ) => {
      try {
        const repairPriorityPayload = {
          isEmergency: workOrderRepair.isEmergency,
          repairType: workOrderRepair.sorCode,
          description: workOrderRepair.description,
          channel: SELECTED_REPORTING_METHOD,
          raisedDate: new Date().toISOString(),
          isAnaType: isAnaTypeValue,
          tenureCode:
            customerDetails?.tenanciesSummaries?.[0]?.tenancySummary
              ?.tenancyTenureCode ?? "",
          isV14Upgradable: completeEDSessionData.isV14Eligibile ?? false,
          workProgramme: completeEDSessionData?.workProgram,
          premisesType: assetDetails?.assetType,
          hasSeasonalPriority:selectedRepairReason?.[0]?.hasSeasonalPriority ?? false
        };
        const result = await api.repairPriorityDetermination(
          repairPriorityPayload
        );
        return result?.repairPriority ?? "";
      } catch (err) {
        logError("repairPriorityDetermination failed", err);
        return;
      }
    },
    []
  );

  // Generates the problem list array for the iframe config, based on asset type/status/tenancy group
  const getProblemListArr = async (
    assetDetails: any,
    selectedTenancyGroup: string | undefined,
    getMatchingTenancyGroup: (tenancyGroup: string) => Promise<string>
  ): Promise<string[]> => {
    if (
      assetDetails?.assetType === Helper.AssetTypeName.Property &&
      assetDetails?.assetStatus === "OCCP"
    ) {
      if (selectedTenancyGroup) {
        const tenancyValue = await getMatchingTenancyGroup(selectedTenancyGroup);
        return tenancyValue ? [tenancyValue] : [];
      } else {
        return [];
      }
    } else if (assetDetails?.assetType !== Helper.AssetTypeName.Property) {
      return ["communal"];
    } else {
      return [];
    }
  };

  // Opens the back/exit modal when user clicks back
  const backButtonHandler = () => setIsBackModalOpen(true);

  // Closes modal and navigates to trade category page when user chooses to leave
  const handleBackLeave = async () => {
    await triggerEDAbandonedStatus();
    setIsBackModalOpen(false);
    props.history.replace("/RepairTradeCategory");
  };


  const exitButtonHandler = async () => {
    await triggerEDAbandonedStatus();
    dispatch(backToRepairAsset(false))
    props.history.push("/LandingView");
  };

  const triggerEDAbandonedStatus = async () => {
    //Need seperate function as WO wont exist till after ED journey returns correct data, & WO API generates repair
    try {
      await api.updateRepairDiagnosticsSession(sessionIdRef.current, {
        status: Helper.RepairEnhancedDiagnosticStatus.Abandoned
      });
    } catch (err) {
      logError(`Error updating diagnostics session to ${Helper.RepairEnhancedDiagnosticStatus.Abandoned}`, err);
    }
  }

  // Handler for when the iframe job completes; processes session, creates work order, redirects
  const loggedJobHandler = useCallback(
    async (jobIds: any) => {
      try {
        showLoader(true);

        const MAX_RETRIES = 3;
        let completeEDSessionData: any;
        let attempt = 0;
        let isFinalDataEmpty = true;

        while (attempt < MAX_RETRIES && isFinalDataEmpty) {
          completeEDSessionData = await api.getRepairDiagnosticFinalStatus(sessionIdRef.current);

          isFinalDataEmpty =
            !completeEDSessionData?.sorCode ||
            !completeEDSessionData?.smv ||
            completeEDSessionData?.smv <= 0 ||
            !completeEDSessionData?.problemCategory ||
            !completeEDSessionData?.problem ||
            !completeEDSessionData?.elementCode ||
            !completeEDSessionData?.workProgram ||
            !completeEDSessionData?.location;

          if (isFinalDataEmpty) {
            attempt += 1;
            if (attempt < MAX_RETRIES) {
              await new Promise((res) => {
                const t = setTimeout(res, 1000);
                timeoutIdsRef.current.push(t);
              });
            }
          }
        }
        if (isFinalDataEmpty) {
          logError(`Final response for repair diagnostics is incomplete after ${attempt} attempt(s):`, completeEDSessionData);
          props.history.push("/GenericError");
          return;
        }
        dispatch(setRepairDiagnosticFinalData(completeEDSessionData));
        const workOrderRepair = new CurrentRepair();
        const _premisesTypeId: any =
          PremisesTypeId[
          assetDetails?.assetType as keyof typeof PremisesTypeId
          ];
        workOrderRepair.repairReason = completeEDSessionData?.repairDescription;
        workOrderRepair.description = completeEDSessionData?.repairDescription;
        workOrderRepair.premisesId = assetDetails?.id;
        workOrderRepair.premisesTypeId = workOrderRepair.premisesTypeId = assetDetails?.assetType === DIVIDED_BLOCK ? _.findKey(Helper.AssetTypeId, (name) => name === DIVIDED_BLOCK) : _premisesTypeId
        workOrderRepair.customerIdentifier = customerID;
        workOrderRepair.isEmergency = completeEDSessionData?.priority === RepairPriorityCode.Emergency;
        workOrderRepair.premisesUniqueReference = assetDetails?.uprn;
        const _address = new Address();
        _address.addressLine1 =
          customerDetails?.addresses?.postalAddresses[0]?.addressLine1;
        _address.addressLine2 =
          customerDetails?.addresses?.postalAddresses[0]?.addressLine2;
        _address.addressLine3 =
          customerDetails?.addresses?.postalAddresses[0]?.addressLine3;
        workOrderRepair.premisesAddress = _address;
        workOrderRepair.sorCode = completeEDSessionData?.sorCode;
        workOrderRepair.customerName = new CustomerName({
          title: customerDetails?.person?.name?.title,
          givenName: customerDetails?.person?.name?.givenName,
          surname: customerDetails?.person?.name?.surname,
        });

        const determinedPriority = await fetchDeterminedPriority(
          api,
          workOrderRepair,
          isAnaTypeValue,
          customerDetails,
          completeEDSessionData,
          assetDetails
        );
        if (!determinedPriority) {
          logError("Determined priority is empty or undefined", determinedPriority);
          props.history.push("/GenericError");
          return;
        }
        dispatch(priorityCode(determinedPriority));
        workOrderRepair.priority = determinedPriority;
        const selectedPriority = Helper.RepairPriorityLabels[workOrderRepair.priority as keyof typeof Helper.RepairPriorityLabels] || workOrderRepair.priority;
        dispatch(selectedRepairReasonDefaultPriority(selectedPriority));
        dispatch(selectedRepairReasonPriorityOverride(workOrderRepair.priority));

        workOrderRepair.reportingMethodId = Helper.RepairReportingMethods[SELECTED_REPORTING_METHOD as keyof typeof Helper.RepairReportingMethods];
        workOrderRepair.reportingMethod = Helper.RepairReportingMethodLabels[workOrderRepair.reportingMethodId as keyof typeof Helper.RepairReportingMethodLabels];
        workOrderRepair.notifyAll = false;
        workOrderRepair.eTag = customerDetails?.eTag;

        const maintsResp = assetDetails?.maintenanceResponsibilities?.find((x: any) => x.type === "MAINTSRESP")?.value ?? "";
        var repairResp = assetDetails?.maintenanceResponsibilities?.find((resp: any) => resp.type === completeEDSessionData?.elementCode)?.value ?? "";

        workOrderRepair.eligibilityOutcomes = await getEligibilityResponse(
          api,
          assetDetails,
          maintsResp,
          completeEDSessionData,
          logError
        );

        dispatch(userChosenEligibilityErrorCode(workOrderRepair.eligibilityOutcomes));
        dispatch(getMaintResp(maintsResp));
        dispatch(getRepairResp(repairResp));

        workOrderRepair.assetIdentifier = tenanciesData?.premisesIds ?? "";
        workOrderRepair.repairLocationId = await getRepairLocationId(
          assetDetails?.assetType ?? "",
          completeEDSessionData?.location ?? ""
        );
        workOrderRepair.repairCauseId = 1;
        workOrderRepair.repairCause = Helper.RepairCauseLabels[workOrderRepair.repairCauseId as keyof typeof Helper.RepairCauseLabels];;

        workOrderRepair.isCatalyst = isCatalyst;
        workOrderRepair.isCausingDampAndMould = (sordamp?.split(",") ?? []).includes(completeEDSessionData?.sorCode);
        workOrderRepair.repairDiagnosticsSessionIdentifier = sessionIdRef.current;
        workOrderRepair.isEnhancedDiagnosticsRepair = true;
        workOrderRepair.elementCode = completeEDSessionData?.elementCode ?? "";
        workOrderRepair.smv = completeEDSessionData?.smv ?? 0;
        workOrderRepair.workProgramme = completeEDSessionData?.workProgram ?? "";
        workOrderRepair.isDraft = true;
        const worksOrderRequest = new WorksOrderRequest(workOrderRepair);
        const woRequestParameters = worksOrderRequest.createWorkOrderParameter(workOrderRepair, isEligibleForEnhancedDiagnostics && isEligibleTradeforED);
        const apiResponse = await api.createWorkOrderRepair(woRequestParameters);

        if (!apiResponse?.location || apiResponse.location.length === 0) {
          logError("Work order creation failed or invalid response", apiResponse);
          props.history.push("/GenericError");
          return;
        }
        // Only delete the existing work order if it is in draft (regardless of Enhanced Diagnostics)
        const { isDraft = false } = existingWorkOrder || {};
        if (isDraft) {
          await deleteExistingWorkOrder();
        }

        const workorderLocation = apiResponse.location.split("/");
        workOrderRepair.id = workorderLocation[4];
        workOrderRepair.worksOrderIdentifier = workorderLocation[4];

        dispatch(workOrderRepairObject(workOrderRepair));
        const worksOrder = new WorksOrderRequest(workOrderRepair);
        dispatch(workOrderData(worksOrder));
        dispatch(userChosenSOR(workOrderRepair.sorCode));
        dispatch(userChosenRepairLocationId(workOrderRepair.repairLocationId));
        dispatch(userChosenHighLevelDescription(workOrderRepair.repairReason));
        dispatch(userChosenNotifyAll(false));
        dispatch(userChosenEmergencyQuestion(workOrderRepair.isEmergency ? "Yes" : "No"));
        if (isHHSRSEnabled && isHHSRSRole) {
          props.history.push("/hhsrs")
        }
        else if (workorderChoosePriorityOfRepairPermission || determinedPriority === Helper.RepairPriorityCodes.EME) {
          props.history.push("/RepairEDEmergency");
        }
        else {
          //need to check if we need hhsrs here also
          props.history.push("/DuplicateRepair");
        }
      } catch (error) {
        logError("loggedJobHandler sequence", error);
        props.history.push("/GenericError");
      } finally {
        const timeoutId = setTimeout(() => {
          showLoader(false);
          const iframe = document.getElementById(PARENT_IFRAME_ID);
          if (iframe && iframe.parentNode) {
            iframe.parentNode.removeChild(iframe);
          }
          delete window.activeHousingIframeConfig;
          sessionIdRef.current = "";
        }, 100);
        timeoutIdsRef.current.push(timeoutId);
      }
    },
    [
      showLoader,
      PARENT_IFRAME_ID,
      dispatch,
      props.history,
      assetDetails,
      customerDetails,
      customerID,
      isAnaTypeValue,
      tenanciesData?.premisesIds,
      isCatalyst,
      sordamp,
      fetchDeterminedPriority,
      getEligibilityResponse,
      getRepairLocationId,
      isEligibleForEnhancedDiagnostics,
      isEligibleTradeforED,
      workorderChoosePriorityOfRepairPermission,
      deleteExistingWorkOrder,
      existingWorkOrder
    ]
  );

  // useEffect on mount: orchestrates iframe setup, diagnostics session, token, loader, cleanup
  // Uses requestAnimationFrame (poll DOM for safe timing) and MutationObserver (watch for iframe creation)
  useEffect(() => {
    if (!isEligibleForEnhancedDiagnostics || !isEligibleTradeforED) {
      props.history.push("/LandingView");
      return;
    }
    if (hasInitialized.current) return;
    hasInitialized.current = true;
    resetReduxFromNewEmergencyAndDuplicatePages();
    removeIframeContainer();
    createIframeContainer();
    const observer = observeIframeAndSetLoading(CONTAINER_SELECTOR, setIsLoading);

    (async function () {
      try {
        setIsLoading(true);
        const problemListArr = await getProblemListArr(assetDetails, selectedTenancyGroup, getMatchingTenancyGroup);

        let customerId = customerID;
        if (!customerId) {
          const queryParams = {
            searchText: "",
            limit: 1,
            skip: 0,
            customerType: "",
            orderBy: "",
            tenancyType: "",
            uprn: assetDetails?.uprn,
          };

          let customerSearchApi = null;
          try {
            customerSearchApi = await api.getCustomerData(queryParams);
          } catch (err) {
            logError("getCustomerData failed", err);
          }

          const customerCallResponse = customerSearchApi?.models ?? [];
          if (customerCallResponse.length > 0 && customerCallResponse[0]?.id) {
            customerId = customerCallResponse[0].id;
            dispatch(getCustomerId(customerId));

            try {
              const customerDetailsResponse = await api.getCustomer(customerId);
              dispatch(saveCustomerDetails(customerDetailsResponse));
            } catch (err) {
              logError("getCustomer failed", err);
            }
          }
        }

        const sessionPayload = {
          channel: "ColleagueSelfService",
          customerIdentifier: customerId,
          assetIdentifier: assetDetails?.id || assetId,
        };
        const response = await api.createRepairDiagnosticsSession(sessionPayload);
        const session = new RepairDiagnosticsSessionResponse(response);
        sessionIdRef.current = session.repairDiagnosticsSessionIdentifier;
        try {
          await api.updateRepairDiagnosticsSession(sessionIdRef.current, {
            repairDiagnosticsSessionIdentifier: sessionIdRef.current,
            status: Helper.RepairEnhancedDiagnosticStatus.Launched,
            assetIdentifier: assetDetails?.id ?? assetId,
            customerIdentifier: customerId,
          });
        } catch (err) {
          logError(`Error updating diagnostics session to ${Helper.RepairEnhancedDiagnosticStatus.Launched}`, err);
        }

        const tokenParamValues = {
          integrationtype: INTEGRATION_TYPE,
          staff: "true",
          exclusionlist: [],
          ...(problemListArr && problemListArr.length > 0 ? { problemlist: problemListArr } : {}),
          ...(assetDetails?.statuses?.isInDefects ? { "ca-defects": "true" } : {}),
          propertytype: propertyType,
          token: "",
          "x-diagnostics-session-id": getSessionIdWithEnv(sessionIdRef.current),
        };

        // Pick only valid, non-empty values for the iframe config
        const iframeValues = _.pickBy(tokenParamValues, value => value !== "" && value !== null && value !== undefined && (!Array.isArray(value) || value.length > 0));

        window.activeHousingIframeConfig = {
          container: PARENT_IFRAME_ID,
          customer: CUSTOMER_STG,
          onNavScrollToIncludeMargin: false,
          overrideUrl: ACTIVE_HOUSING_STAGING_REPAIRS_URL,
          path: `/category/${plentificCategoryId}`,
          values: iframeValues,
          integrationCallbacks: { loggedJobHandler },
        };

        // Convert iframe values (except token) into the API's expected format
        const tokenParameters = _.map(
          _.omitBy(
            window.activeHousingIframeConfig.values,
            (v, k) => k === "token"
          ),
          (value, key) => ({
            key,
            value: Array.isArray(value) ? value : [String(value)],
          })
        );

        const tokenResponse = await api.getRepairDiagnosticToken(tokenParameters);

        if (tokenResponse?.repairDiagnosticsToken) {
          window.activeHousingIframeConfig.values.token = tokenResponse.repairDiagnosticsToken;
        }

        await waitForIframeAndInjectScript(PARENT_IFRAME_ID, SCRIPT_ID, injectActiveHousingScript);
      } catch (e) {
        logError("diagnostics session init sequence", e);
        setIsLoading(false);
      }
    })();
    // When leave this page, everything here (iframediv, loader, timeouts) gets cleaned up.
    return () => {
      // Clear timeouts
      timeoutIdsRef.current.forEach((id) => clearTimeout(id));
      timeoutIdsRef.current = [];

      // Other cleanups
      observer && observer.disconnect();
      Helper.removeActiveHousingScript(SCRIPT_ID);
      delete window.activeHousingIframeConfig;
      sessionIdRef.current = "";
      const containerDiv = document.getElementById(PARENT_IFRAME_ID);
      if (containerDiv) {
        containerDiv.remove();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container id="reploc-container" role="main" className="ctn-fht">
      <div className="container mx-auto mb-0 md:mb-14 lg:mb-10 px-0 xl:px-0">
        <div className="back-and-journey">
          <p className="text-teal text-xl mt-4 mb-8">
            <button
              className="back-btn"
              data-testid="backButtonHandler"
              onClick={backButtonHandler}
            >
              <i className="fas fa-angle-left mr-2" />
              {t("back")}
            </button>
          </p>
          <ExitModalAndSave
            id="ed-back-exitmodal"
            open={isBackModalOpen}
            modalTitle={
              <span style={{ color: "#A64CA6", fontWeight: 600 }}>
                {t("ED_BACKMODAL_TITLE")}
              </span>
            }
            modalSubtitle={
              <>
                {t("ED_BACKMODAL_SUBTITLE_PART1")}
                <strong>&quot;{t("ED_BACKMODAL_SUBTITLE_BOLD")}&quot;</strong>
                {t("ED_BACKMODAL_SUBTITLE_PART2")}
              </>
            }
            stayButtonText={t("ED_BACKMODAL_STAY")}
            leaveButtonText={t("ED_BACKMODAL_LEAVE")}
            closeAfterSaving={false}
            canSaveAndExit={false}
            showBackOnly={true}
            onLeave={handleBackLeave}
            onClose={() => setIsBackModalOpen(false)}
          />
          <RepairJourney />
        </div>
      </div>

      <div className="container w-100 position-relative">
        {isLoading && (
          <div
            className="position-absolute top-0 bottom-0 mt-4 start-0 end-0 d-flex justify-content-center align-items-center"
            style={{ backgroundColor: "rgba(255,255,255,0.8)", zIndex: 10 }}
          >
            <LoadingSpinner />
          </div>
        )}
        <div id={PARENT_IFRAME_ID} className={IFRAME_CLASSNAME} />
        <div
          id={DIAGNOSTICS_SESSION_LOADER}
          className="d-none position-absolute mt-4 bottom-0 start-0 end-0 d-flex justify-content-center align-items-center"
          style={{ backgroundColor: "rgba(255,255,255,0.8)", zIndex: 10 }}
        >
          <LoadingSpinner />
        </div>
      </div>
      <div
        id="ed-back-bottom-btn"
        className="container flex justify-between items-center pb-4 md:pb-8"
      >

        {!isLoading && (
          <ExitModalAndSave
            data-testid="ed-back-exitmodal-bottom"
            id="ed-back-exitmodal-bottom"
            exit={exitButtonHandler}
            closeAfterSaving={false}
            canSaveAndExit={false}
            showBackOnly={false}
          />
        )}

      </div>
    </Container>
  );
};
export default RepairEnhancedDiagnostic;
